/**
 * Wan on fal.ai (Phase 1, U4): Wan 2.2 (legacy, frames-based), Wan 2.6 and Wan 2.7.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * These descriptors reproduce TODAY's behaviour byte for byte (P1 is price- and payload-neutral).
 * Replaces, once the family is flipped and cleaned (C1):
 *   - falai.ts video route: the Wan 2.6/2.7 pre-reserve resolution 400, the 'wan-2.2' and
 *     'wan-2.6'/'wan-2.7' switch arms, and (for wan-2.2) the post-arm "charged a tier the payload
 *     does not carry" 500;
 *   - ProviderService.generateVideoFalAI: the wan-2.2 and wan-2.6/2.7 branches;
 *   - CreditService's generic ladder for all three rows (migration 133 for 2.7).
 *
 * All three bill through the generic tail ceil(credits_per_unit × ceil(units) × ladder(resolution)).
 * wan-2.2 keeps units.stage 'raw' (the legacy mappers hand it wan22NumFrames(d) / 16, the seconds
 * rendered); wan-2.6 / 2.7 use 'resolved' — the value the engine wires, before the wire's rounding.
 *
 * Schemas: server/config/models/__schemas__ (scaffold-fal, fal OpenAPI oracle, 2026-09-19).
 * wan/v2.6/* is on the BARE `wan/` namespace (both slugs return 200 on 2026-09-19, including
 * wan/v2.6/text-to-video, which an earlier check had reported dead).
 */
import type { LegacyWire, ModelDescriptor, Param } from '../types';
import { IMG, W_IMG } from '../_shared';

const ORACLE_DATE = '2026-09-19';

const WAN_NEGATIVE_PROMPT = 'low resolution, error, worst quality, low quality, defects';

// ─── Wan 2.2 (A14B) ────────────────────────────────────────────────────────────────────────
// No `duration` field: length is num_frames at 16 fps. Legacy (both surfaces):
//   num_frames = min(161, max(17, round((Number(duration) || 5) * 16))), frames_per_second = 16.
// The editor forwards aspect_ratio only for 16:9 / 9:16 (both modes); headless forwards
// `aspectRatio || '16:9'` verbatim. Neither surface sends `resolution`.
export const WAN_22: ModelDescriptor = {
  id: 'wan-2.2', provider: 'falai', face: 'video', displayName: 'Wan 2.2', ledgerName: 'Wan 2.2 (Legacy)', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/wan/v2.2-a14b/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'fal-ai/wan/v2.2-a14b/image-to-video', ports: [IMG()], media: { image: W_IMG } },
  },
  params: [
    { id: 'duration', type: 'number', uiValues: [3, 5], default: 5, role: 'units',
      // `Number(duration) || 5`: 0 is falsy -> 5 (alias); garbage -> 5 (coerce default); negatives
      // and fractions stay and are scaled, then clamped by the wire (-1 -> 17 frames, 7.5 -> 120).
      aliases: { '0': 5 }, invalid: { coerce: { kind: 'default' } },
      wire: { kind: 'scale', field: 'num_frames', factor: 16, min: 17, max: 161 },
      ui: { label: 'Duration', tier: 'basic' } },
    { id: 'resolution', type: 'enum', values: ['480p', '580p', '720p'], uiValues: ['480p', '720p'], default: '720p',
      normalize: 'lower', role: 'tier',
      // The ladder reads '768p' and '2k' as its 720p rung (getResolutionTier's fallback).
      aliases: { '768p': '720p', '2k': '720p' },
      // Editor: a tier above 720p used to reserve, 500 ("charged at the 1080p tier, but the
      // payload carries no resolution") and refund; now a pre-reserve 400 (class B1).
      // Headless has no such guard: it bills the raw ladder tier and renders at fal's default
      // (decisionsForOwner, U4). Billing-only: fal is never sent a resolution.
      invalid: { editor: 'reject', headless: 'passthrough' }, wire: false,
      // Editor: legacy's only guard is "the ladder tier is above 720p" (the post-arm 500). Every
      // value the shared ladder reads as <= 1x ('garbage', '1280x720', '512', ...) was served at
      // fal's 720p default and billed at that rung. Passing it through bills the same rung from
      // the same function (the resolution never reaches the wire).
      domainRules: { editor: [{
        when: { ladder: { curve: 'legacy-resolution-v1', lte: 1 } }, then: 'passthrough',
        why: "falai.ts wan-2.2 arm: only a ladder tier above 720p is refused; anything the ladder bills at <= 1x is served at fal's default",
      }] },
      ui: { label: 'Resolution', tier: 'basic' } },
    { id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16'], default: null,
      // Editor: only 16:9 / 9:16 are forwarded, anything else is dropped (coerce -> null -> not
      // emitted). Headless: `aspectRatio || '16:9'` verbatim (the legacy.wire pair below).
      invalid: { editor: { coerce: { kind: 'default' } }, headless: 'passthrough' },
      wire: { field: 'aspect_ratio' }, ui: { label: 'Aspect ratio', tier: 'basic' } },
  ],
  fixed: { frames_per_second: 16 },
  legacy: {
    wire: [
      { field: 'aspect_ratio', value: '16:9', surface: 'headless',
        why: "headless generic builder default (`aspectRatio || '16:9'`)", retire: 'owner-approval' },
      { field: 'aspect_ratio', fromParam: 'aspectRatio', surface: 'headless',
        why: 'headless generic builder forwards the raw ratio, also on image-to-video', retire: 'owner-approval' },
    ],
    omits: [
      { field: 'aspect_ratio',
        why: 'editor forwards only 16:9 / 9:16; 1:1 (in both schemas) and every other ratio is dropped, so fal uses 16:9 (t2v) / auto (i2v)' },
      { field: 'resolution',
        why: 'never sent on either surface: fal renders its 720p default while the ladder bills the requested tier (480p = 0.75x; headless also bills 1080p/4k at 1.5x/3x)' },
      { field: 'video_quality', why: 'never sent: fal default "high"' },
    ],
    notes: [
      'Editor first-last-frame with two images is refused 400 before reserving (falai.ts, 2860433: the VEO-shaped branch that posted it to the T2V endpoint was retired with the fal.ai VEO 3.1 model); the engine refuses it in resolve() (no flf mode, UNSUPPORTED_MODE).',
      "Editor resolution strings the ladder bills at <= 1x but that are not resolution names (e.g. 'garbage') are served at fal's 720p default and billed at that rung on both paths (domainRules: ladder <= 1 -> passthrough).",
      "CLOSED by d01af92: editor duration '' / null / 'abc' used to reserve 0 / NaN credits and render ~5 s. The route now replaces an unusable duration with 5 before reserving and bills wan22NumFrames(d) / 16, the seconds it renders; the editor mapper hands the engine that same value.",
    ],
  },
  pricing: {
    unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'generic', policy: 'fal-video-35',
    skus: [{
      id: 'wan-2.2', rate: { ladder: { base: 37, by: 'resolution', curve: 'legacy-resolution-v1' } },
      vendor: {
        usd: { '720p': 0.08, '580p': 0.06, '480p': 0.04 }, per: 'second', field: 'pricingInfoOverride',
        source: 'fal.ai/models/fal-ai/wan/v2.2-a14b/text-to-video',
        text: 'Your request will cost **$0.08** per **video second** for **720p**, **$0.06** per **video second** for **580p**, **$0.04** per **video second** for **480p**. Video seconds are calculated at 16 frames per second.',
        verified: ORACLE_DATE,
      },
      registry: { displayName: 'Wan 2.2 (Legacy)', operationType: 'video', publishedTier: '720p', flatRate: false },
    }],
  },
  schemaVerified: ORACLE_DATE,
};

// ─── Wan 2.6 / Wan 2.7 ─────────────────────────────────────────────────────────────────────
// Oracle facts: `resolution` enum ["720p","1080p"] default "1080p" (always emitted); aspect_ratio
// on text-to-video only; 2.7 `duration` INTEGER enum 2..15, 2.6 `duration` STRING enum
// ["5","10","15"]; `multi_shots` on 2.6 only.

const WAN_RESOLUTION: Param = {
  id: 'resolution', type: 'enum', values: ['720p', '1080p'], default: '720p', normalize: 'lower', role: 'tier',
  // Editor refuses before reserving today (falai.ts); headless refused AFTER reserving (throw +
  // refund in ProviderService) and now refuses before: class B1.
  invalid: { editor: 'reject', headless: 'reject' },
  wire: { field: 'resolution' }, ui: { label: 'Resolution', tier: 'basic' },   // fal default is 1080p -> always emitted
};

const WAN_ASPECT: Param = {
  id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16', '1:1', '4:3', '3:4'], default: '16:9', onlyModes: ['t2v'],
  // `if (isTextToVideo)` in the editor arm ($t2v: false on the route's t2v-endpoint fallbacks).
  sendWhen: { $t2v: { eq: true } },
  // Editor: sanitizeAspectRatio(raw, '16:9') forwarded as is (21:9 included). Headless: the
  // WAN_ASPECT_RATIOS list, anything else -> '16:9'.
  invalid: { editor: 'passthrough', headless: { coerce: { kind: 'default' } } },
  wire: { field: 'aspect_ratio' }, ui: { label: 'Aspect ratio', tier: 'basic' },
};

const WAN_EDITOR_WIRE: readonly LegacyWire[] = [
  { field: 'enable_prompt_expansion', value: true, surface: 'editor',
    why: 'editor-only constant; equals the fal default (headless never sends it)', retire: 'inert-cleanup' },
  { field: 'negative_prompt', value: WAN_NEGATIVE_PROMPT, surface: 'editor',
    why: 'editor-only constant (headless sends none): changes the output, so retiring it needs approval', retire: 'owner-approval' },
];

const wanPricing = (id: string, name: string, slug: string, text: string): ModelDescriptor['pricing'] => ({
  // 'resolved' since P1-WMLP: bills the value the engine wires (before the wire's own rounding).
  // Editor out-of-enum values pass through, so resolved == raw there (7.5 bills ceil(7.5) = 8 and
  // wires Math.round(7.5) = 8, as legacy); headless wan-2.7 clamps 2..15, and since d01af92 legacy
  // bills that clamped length (headlessFalVideoSeconds) — 30 s bills and renders 15 s on both.
  unit: 'second', units: { param: 'duration', stage: 'resolved' }, rounding: 'generic', policy: 'fal-video-35',
  skus: [{
    // == the generic ladder over this two-value domain: 46 × 1 at 720p, ceil(46 × 1.5) = 69 at 1080p.
    id, rate: { by: ['resolution'], table: { '720p': 46, '1080p': 69 }, fallbackKey: '720p' },
    vendor: { usd: { '720p': 0.10, '1080p': 0.15 }, per: 'second', field: 'pricingInfoOverride', source: `fal.ai/models/${slug}`,
              text, verified: ORACLE_DATE },
    marginException: 'Live rate 46 cr/s (x1.5 = 69) is 1 credit under ceil(usd x 300 / 0.65) = 47 / 70 at both tiers (migration 133 kept 46). P1 is price-neutral; repricing is an owner decision.',
    registry: { displayName: name, operationType: 'video', publishedTier: '720p', flatRate: false },
  }],
});

export const WAN_26: ModelDescriptor = {
  id: 'wan-2.6', provider: 'falai', face: 'video', displayName: 'Wan 2.6', ledgerName: 'Wan 2.6', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'wan/v2.6/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'wan/v2.6/image-to-video', ports: [IMG()], media: { image: W_IMG } },
  },
  params: [
    WAN_RESOLUTION,
    { id: 'duration', type: 'int', values: [5, 10, 15], uiValues: [5, 10, 15], default: 5, role: 'units',
      // Editor: String(Math.round(duration)) of whatever arrives (fal rejects a length outside
      // the enum later, and the poller refunds). Headless: refused AFTER reserving today (throw +
      // refund), before reserving now: class B1.
      invalid: { editor: 'passthrough', headless: 'reject' },
      // `String(Math.round(duration))`: the wire rounds, the reserve bills the raw value.
      wire: { field: 'duration', as: 'string', round: true }, ui: { label: 'Duration', tier: 'basic' } },
    WAN_ASPECT,
    { id: 'multiShots', type: 'bool', default: true,
      // `multiShots = true` destructuring default, then `multiShots !== false`: only a real
      // boolean false turns it off ('false' the string stays on).
      invalid: { coerce: { kind: 'default' } }, wire: false, ui: { label: 'Multi-shot', tier: 'advanced' } },
  ],
  legacy: {
    wire: [
      ...WAN_EDITOR_WIRE,
      { field: 'multi_shots', fromParam: 'multiShots', surface: 'editor',
        why: 'editor-only (headless never sends it; fal i2v default false, t2v default true). FalAIVideoService never sends multiShots, so the node setting is dead and the value is always true (Q3)',
        retire: 'owner-approval' },
    ],
    notes: [
      "Editor duration: legacy sends String(Math.round(x)) and bills ceil(x); the engine passes an out-of-enum value through and `wire.round` rounds it (7.5 -> \"8\", billed ceil(7.5) = 8). An unusable value ('', null, 'abc', <= 0) is 5 on both paths since d01af92.",
      'Editor FLF with ONE image, and i2v with lastImageUrl, go to t2v WITHOUT aspect_ratio (legacy isTextToVideo false); the engine emits it. Needs the legacy t2v flag from the U2 mapper.',
      "CLOSED (was a flip blocker): blank editor duration used to reserve 0 while the engine wired the in-enum default 5, a paid render for free. d01af92 legacy reserves and wires 5; the engine bills an unusable raw units value at the value it wires (engine computeUnits / isUsableUnits), so a direct engine call reserves 5 too.",
    ],
  },
  pricing: wanPricing('wan-2.6', 'Wan 2.6', 'wan/v2.6/text-to-video',
    'Your request will cost  **$0.10** per second for **720p**, **$0.15** per second for **1080p**.'),
  schemaVerified: ORACLE_DATE,
};

export const WAN_27: ModelDescriptor = {
  id: 'wan-2.7', provider: 'falai', face: 'video', displayName: 'Wan 2.7', ledgerName: 'Wan 2.7', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/wan/v2.7/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'fal-ai/wan/v2.7/image-to-video', ports: [IMG()], media: { image: W_IMG } },
    // end_image_url / audio_url exist in the i2v schema; legacy never sent them -> P2.
  },
  params: [
    WAN_RESOLUTION,
    { id: 'duration', type: 'int', values: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], uiValues: [5, 10, 15], default: 5, role: 'units',
      // Editor: Math.round(duration) of whatever arrives, unclamped (0, -1, 20 reach fal).
      // Headless: Math.min(15, Math.max(2, Math.round(seconds))).
      invalid: { editor: 'passthrough', headless: { coerce: { kind: 'clamp', min: 2, max: 15 } } },
      // `Math.round(duration)`: the wire rounds, the reserve bills the raw value.
      wire: { field: 'duration', as: 'integer', round: true }, ui: { label: 'Duration', tier: 'basic' } },
    WAN_ASPECT,
  ],
  legacy: {
    wire: WAN_EDITOR_WIRE,
    notes: [
      "Editor duration: legacy sends Math.round(x) and bills ceil(x); the engine passes an out-of-enum value through and `wire.round` rounds it (7.5 -> 8, billed ceil(7.5) = 8). An unusable value ('', null, 'abc', <= 0) is 5 on both paths since d01af92.",
      'Editor FLF with ONE image, and i2v with lastImageUrl, go to t2v WITHOUT aspect_ratio (legacy isTextToVideo false); the engine emits it. Needs the legacy t2v flag from the U2 mapper.',
      "CLOSED (was a flip blocker): blank editor duration used to reserve 0 while the engine wired the in-enum default 5, a paid render for free. d01af92 legacy reserves and wires 5; the engine bills an unusable raw units value at the value it wires (engine computeUnits / isUsableUnits), so a direct engine call reserves 5 too.",
    ],
  },
  pricing: wanPricing('wan-2.7', 'Wan 2.7', 'fal-ai/wan/v2.7/text-to-video',
    'Your request will cost **$0.1** per second for 720p resolution. For 1080p your request will cost **$0.15** per second. '),
  schemaVerified: ORACLE_DATE,
};

export const WAN_MODELS: readonly ModelDescriptor[] = [WAN_22, WAN_26, WAN_27];
